// ============================================================
// rows - the library's records as the backends store them, and back.
//
// Tags and the last note are lists and objects the app stringifies, so they
// travel as TEXT (a json column reorders keys: the schema's text-versus-json
// rule). A row read from the wire is untrusted: every field gets a default,
// so one half-written row shows as a plain card rather than breaking the page.
// ============================================================
import { ASSET_KINDS, ROOT, type AssetKind, type AssetRow, type FolderRow, type Note } from './types';

/** An asset row as stored: the same fields, with `tags` and `lastNote` as text. */
export type AssetDoc = Omit<AssetRow, 'id' | 'tags' | 'lastNote'> & { tags: string; lastNote: string };
export type FolderDoc = Omit<FolderRow, 'id'>;

const num = (v: unknown, fallback = 0) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const str = (v: unknown, fallback = '') => (typeof v === 'string' ? v : fallback);

function parsed<T>(text: unknown, fallback: T): T {
  if (typeof text !== 'string' || !text) return fallback;
  try {
    return JSON.parse(text) as T;
  } catch {
    // A torn note or tag list shows as none; the row itself still reads.
    return fallback;
  }
}

function tagsOf(text: unknown): string[] {
  const list = parsed<unknown>(text, []);
  return Array.isArray(list) ? list.filter((t): t is string => typeof t === 'string') : [];
}

export function docFromAsset(row: AssetRow): AssetDoc {
  const { id: _id, tags, lastNote, ...rest } = row;
  return { ...rest, tags: JSON.stringify(tags), lastNote: lastNote ? JSON.stringify(lastNote) : '' };
}

/**
 * A row as this build reads it, or null for a kind it does not know.
 *
 * A NEWER build may add kinds. Reading one as a map (as this did until v11.87.13) showed it
 * as a broken map and, worse, a rename or move wrote `kind: 'map'` back over it. A row of an
 * unknown kind is left alone instead: hidden, never written, its bytes still counted.
 */
export function assetFromDoc(id: string, doc: Partial<Record<keyof AssetDoc, unknown>>): AssetRow | null {
  if (!ASSET_KINDS.includes(doc.kind as AssetKind)) return null;
  const kind = doc.kind as AssetKind;
  return {
    id,
    kind,
    name: str(doc.name, 'Untitled'),
    folderId: str(doc.folderId, ROOT),
    tags: tagsOf(doc.tags),
    nextVersion: num(doc.nextVersion, 1),
    version: num(doc.version),
    size: num(doc.size),
    bytes: num(doc.bytes),
    thumbVersion: num(doc.thumbVersion),
    lastNote: parsed<Note | null>(doc.lastNote, null),
    createdAt: num(doc.createdAt),
    updatedAt: num(doc.updatedAt),
    deletedAt: num(doc.deletedAt),
    trashedFrom: str(doc.trashedFrom, ROOT),
  };
}

/** The bytes a row holds, whatever its kind: a row this build cannot read still fills the quota. */
export function bytesOfDoc(doc: Partial<Record<keyof AssetDoc, unknown>>): number {
  return num(doc.bytes);
}

export function folderFromDoc(id: string, doc: Partial<Record<keyof FolderDoc, unknown>>): FolderRow {
  return {
    id,
    name: str(doc.name, 'Folder'),
    parentId: str(doc.parentId, ROOT),
    createdAt: num(doc.createdAt),
    updatedAt: num(doc.updatedAt),
  };
}

/** A new asset's row, before its first version lands. */
/** A new folder row under `parentId`. */
export function newFolderRow(id: string, name: string, parentId: string, now: number): FolderRow {
  return { id, name, parentId, createdAt: now, updatedAt: now };
}

/** Live (not in the Trash) and with a whole version to read: what can be opened, copied or exported. */
export const isWhole = (a: Pick<AssetRow, 'deletedAt' | 'version'>): boolean => !a.deletedAt && a.version > 0;

export function newAsset(id: string, kind: AssetKind, name: string, folderId: string, now: number): AssetRow {
  return {
    id,
    kind,
    name,
    folderId,
    tags: [],
    nextVersion: 1,
    version: 0,
    size: 0,
    bytes: 0,
    thumbVersion: 0,
    lastNote: null,
    createdAt: now,
    updatedAt: now,
    deletedAt: 0,
    trashedFrom: ROOT,
  };
}
